/**
 * useTheme — VÉRAULT per-surface theme architecture (Wave 30.4.3).
 * ───────────────────────────────────────────────────────────────
 * Each surface (client portal, advisor portal, public site) has its
 * own independent day/night preference. The toggle inside a surface
 * controls ONLY that surface; preferences do not bleed across.
 *
 *   • `useTheme("client")`   → reads/writes `vt-theme-client`
 *   • `useTheme("advisor")`  → reads/writes `vt-theme-advisor`
 *   • Public site            → no toggle yet; ALWAYS light
 *
 * Implementation: a single `data-theme` attribute on `<html>` is still
 * the global selector, but it's NOW route-scoped — we apply it on
 * portal mount and remove it on portal unmount / public-site
 * navigation. This keeps the existing CSS-variable architecture
 * intact while making theme leakage impossible.
 *
 * Legacy migration: a `vt-theme` value from earlier waves migrates
 * once into `vt-theme-client` (since that's where the toggle lived).
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEYS = {
  client: "vt-theme-client",
  advisor: "vt-theme-advisor",
};
const VALID = ["light", "dark"];
const LEGACY_KEY = "vt-theme";

function storageKeyFor(scope) {
  return STORAGE_KEYS[scope] || STORAGE_KEYS.client;
}

function migrateLegacy() {
  if (typeof window === "undefined") return;
  try {
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (!legacy || !VALID.includes(legacy)) return;
    // Move legacy preference to client scope (where the toggle has lived).
    if (!window.localStorage.getItem(STORAGE_KEYS.client)) {
      window.localStorage.setItem(STORAGE_KEYS.client, legacy);
    }
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* noop */
  }
}

function readStored(scope) {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(storageKeyFor(scope));
    return VALID.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function systemPreference() {
  if (typeof window === "undefined") return "light";
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function apply(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

function clear() {
  if (typeof document === "undefined") return;
  document.documentElement.removeAttribute("data-theme");
}

/**
 * Hook used by `ThemeToggle` inside a portal shell.
 * Scope MUST be passed explicitly to keep surfaces decoupled.
 */
export function useTheme(scope = "client") {
  const [theme, setThemeState] = useState(() => {
    migrateLegacy();
    return readStored(scope) || systemPreference();
  });

  // Apply on mount and whenever theme changes.
  useEffect(() => {
    apply(theme);
  }, [theme]);

  // Respond to system theme changes when the user hasn't picked explicitly.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (readStored(scope)) return undefined;
    try {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handle = (e) => setThemeState(e.matches ? "dark" : "light");
      mq.addEventListener ? mq.addEventListener("change", handle) : mq.addListener(handle);
      return () => {
        mq.removeEventListener
          ? mq.removeEventListener("change", handle)
          : mq.removeListener(handle);
      };
    } catch {
      return undefined;
    }
  }, [scope]);

  const setTheme = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setThemeState(next);
    try {
      window.localStorage.setItem(storageKeyFor(scope), next);
    } catch {
      /* noop */
    }
  }, [scope]);

  const resetToSystem = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKeyFor(scope));
    } catch {
      /* noop */
    }
    setThemeState(systemPreference());
  }, [scope]);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, setTheme, toggle, resetToSystem };
}

/**
 * applyInitialTheme — runs once at app boot from `src/index.js`,
 * BEFORE React mounts, so the first paint matches the user's
 * per-surface preference. Path-aware:
 *
 *   /portal/client/*  → apply vt-theme-client
 *   /portal/advisor/* → apply vt-theme-advisor
 *   anything else     → REMOVE data-theme (public site is always light)
 */
export function applyInitialTheme() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  migrateLegacy();
  const path = window.location.pathname || "";
  let scope = null;
  if (path.startsWith("/portal/client")) scope = "client";
  else if (path.startsWith("/portal/advisor")) scope = "advisor";
  if (!scope) {
    clear();
    return;
  }
  const theme = readStored(scope) || systemPreference();
  apply(theme);
}

/**
 * useScopedThemeEffect — mounted inside PortalShell. Applies the
 * scope's persisted theme on mount + whenever path changes, and
 * REMOVES `data-theme` on unmount so navigating back to the public
 * site never inherits a dark portal preference.
 */
export function useScopedThemeEffect(scope) {
  useEffect(() => {
    if (!scope) return undefined;
    const stored = readStored(scope) || systemPreference();
    apply(stored);
    return () => {
      clear();
    };
  }, [scope]);
}

export default useTheme;
